import { isEqual } from 'lodash'
import React, { ReactElement, useRef, useState } from 'react'
import Close from '../icons/Close'
import { RADOM_COLORS } from '../util/Constants'
import { ManagedPaymentMethod, getPaymentMethods } from '../util/Managed'
import { IconButton } from './Button'
import Checkbox from './Checkbox'
import Dropdown, { DropdownContentHeader, DropdownItem, DropdownTextInput } from './Dropdown'
import { InputLabel } from './Input'
import { SelectedMethodIcon } from './PaymentMethodDisplay'
import Spinner from './Spinner'
import Switch from './Switch'
import Tooltip from './Tooltip'

interface IProps {
  disabled?: boolean
  header?: ReactElement
  placeholderText?: string
  selectedMethods: ManagedPaymentMethod[]
  setSelectedMethods: React.Dispatch<React.SetStateAction<ManagedPaymentMethod[]>>
  isLoading?: boolean
  allowTestnets?: boolean
  testnetOnly?: boolean
  label?: string
  allowedNetworks?: string[]
  filterMethod?: (method: ManagedPaymentMethod) => boolean
  getMethodAvailability?: (method: ManagedPaymentMethod) => { available: boolean, reason?: string }
  hideLabel?: boolean
  hideDisabled?: boolean
  height?: number
  showLabel?: boolean
  hideCheckbox?: boolean
  /** One row per asset ticker: selecting USDC enables every network variant.
   *  Balances are network agnostic now, so payment products offer assets and
   *  the payer picks the network at checkout. */
  groupByAsset?: boolean
}

export const ManagedPaymentMethodSelector = (props: IProps): ReactElement => {
  const [query, setQuery] = useState('')
  const dropdownRef = useRef<Dropdown>(null)
  const queryInputRef = useRef<HTMLInputElement>(null)

  const setDropdown = (open: boolean): void => {
    dropdownRef.current?.setState({ open })
  }

  const onQueryChange = (e: React.ChangeEvent): void => {
    const val = (e.target as HTMLInputElement).value
    if (val !== '') {
      setDropdown(true)
    }
    setQuery(val)
  }

  const [testnetMode, setTestnetMode] = useState(false)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', fontSize: 14 }}>
      {!props.hideLabel && <InputLabel>{props.label ?? 'Payment methods'}</InputLabel>}
      <Dropdown
        ref={dropdownRef}
        disabled={props.disabled}
        preferredAlignment='right'
        selectedContent={
          <>
            { props.isLoading && <Spinner /> }
            {
              props.selectedMethods.length === 0 &&
              <div style={{ display: 'flex', alignItems: 'center', minHeight: props.height ?? 'auto', color: RADOM_COLORS.GRAY_DARKER, fontSize: 13.5 }}>
                {props.placeholderText ?? 'Select methods'}
              </div>
            }
            {
              !props.isLoading && props.selectedMethods.length > 0 &&
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap'
              }}>
                {
                  (props.groupByAsset
                    ? props.selectedMethods.filter((method, i) =>
                      props.selectedMethods.findIndex(m => m.ticker === method.ticker) === i)
                    : props.selectedMethods
                  ).map((method, i) =>
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      minHeight: props.height ?? 'auto'
                    }}>
                      <SelectedMethodIcon method={method} />
                      {props.showLabel && <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <span style={{ color: RADOM_COLORS.BLACK, fontSize: 13.5, fontWeight: 500, lineHeight: 1.2 }}>
                            {method.ticker}
                          </span>
                          <span style={{ color: RADOM_COLORS.GRAY_DARKEST, fontSize: 11.5, lineHeight: 1.2 }}>
                            {props.groupByAsset
                              ? method.currencyName ?? method.name
                              : method.hostChain
                                ? `${method.name} on ${method.hostChain.name}`
                                : method.name}
                          </span>
                        </div>
                      </>
                      }
                    </div>
                  )
                }
              </div>
            }
          </>
        }
        dropdownContent={
          <div>
            <DropdownContentHeader style={{
              borderBottom: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
              zIndex: 1,
              position: 'sticky',
              top: 0,
              background: 'var(--surface, #ffffff)'
            }}>
              <DropdownTextInput
                autoFocus
                ref={queryInputRef}
                type="text"
                value={query}
                onChange={onQueryChange}
                placeholder={'Search...'}
              />
              {
                query !== '' &&
                  <IconButton
                    style={{ position: 'absolute', right: 10, padding: 5 }}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setQuery('')
                    }}>
                    <Close style={{ width: 10, pointerEvents: 'none' }} />
                  </IconButton>
              }
            </DropdownContentHeader>

            <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
              <div style={{ overflow: 'scroll' }}>
                {getPaymentMethods()
                  .filter(m => !m.isHidden)
                  .filter(m => props.filterMethod === undefined || props.filterMethod(m))
                  .filter(m => {
                    if (!props.hideDisabled) {
                      return true
                    }

                    let disabled = m.isDisabled
                    if (props.allowedNetworks && props.allowedNetworks.length > 0) {
                      const networkName = m.hostChain?.name || m.name
                      if (!props.allowedNetworks.includes(networkName)) {
                        disabled = true
                      }
                    }

                    return !disabled
                  })
                  .filter(m => {
                    if (!query) return true
                    const lowercaseQuery = query.toLowerCase()
                    return m.name.toLowerCase().includes(lowercaseQuery) ||
                          m.ticker.toLowerCase().includes(lowercaseQuery) ||
                          m.hostChain?.name.toLowerCase().includes(lowercaseQuery)
                  })
                  .filter(t => (testnetMode || props.testnetOnly) ? t.isTestnet : !t.isTestnet)
                  .filter((p, index, visible) =>
                    !props.groupByAsset || visible.findIndex(m => m.ticker === p.ticker) === index)
                  .map((p, i, visible) => {
                    // Grouped mode: this row stands for every same-ticker
                    // variant, and toggling it toggles them all.
                    const variants = props.groupByAsset
                      ? getPaymentMethods().filter(m =>
                        m.ticker === p.ticker &&
                        !m.isHidden &&
                        !m.isDisabled &&
                        ((testnetMode || props.testnetOnly) ? m.isTestnet : !m.isTestnet) &&
                        (props.filterMethod === undefined || props.filterMethod(m)) &&
                        (!props.getMethodAvailability || props.getMethodAvailability(m).available))
                      : [p]

                    const availability = props.getMethodAvailability?.(p)
                    const unavailable = props.groupByAsset
                      ? variants.length === 0
                      : !!availability && !availability.available

                    let disabled = (props.groupByAsset ? false : p.isDisabled) || unavailable
                    if (!props.groupByAsset && props.allowedNetworks && props.allowedNetworks.length > 0) {
                      const networkName = p.hostChain?.name || p.name
                      if (!props.allowedNetworks.includes(networkName)) {
                        disabled = true
                      }
                    }

                    const groupSelected = variants.length > 0 &&
                      variants.every(v => props.selectedMethods.some(c => isEqual(c, v)))

                    return <DropdownItem
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        minHeight: 52,
                        fontSize: 14,
                        opacity: disabled ? 0.25 : 1
                      }}
                      onClick={e => {
                        if (disabled) {
                          return
                        }

                        if (props.groupByAsset) {
                          if (groupSelected) {
                            props.setSelectedMethods(props.selectedMethods.filter(c =>
                              !variants.some(v => isEqual(v, c))))
                          } else {
                            const additions = variants.filter(v =>
                              !props.selectedMethods.some(c => isEqual(c, v)))
                            props.setSelectedMethods([...props.selectedMethods, ...additions])
                          }
                        } else if (props.selectedMethods.find(c => isEqual(c, p))) {
                          props.setSelectedMethods(props.selectedMethods.filter(c => !isEqual(c, p)))
                        } else {
                          props.setSelectedMethods([...new Set([...props.selectedMethods, p])])
                        }
                        if (props.hideCheckbox) {
                          setDropdown(false)
                        }

                        e.stopPropagation()
                      }}

                    >
                      {!props.hideCheckbox &&
                      <Checkbox
                        size={16}
                        checked={props.groupByAsset
                          ? groupSelected
                          : !!props.selectedMethods.find(cc => isEqual(cc, p))} />}
                      <div style={{
                        width: 25,
                        height: 25,
                        backgroundImage: `url(${p.logo})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      }} />
                      <div style={{ display: 'flex', minWidth: 0, flexDirection: 'column', gap: 2 }}>
                        <span style={{ color: RADOM_COLORS.BLACK, lineHeight: 1.25, fontWeight: 500 }}>{p.ticker}</span>
                        <span style={{ color: RADOM_COLORS.GRAY_DARKEST, fontSize: 12, lineHeight: 1.2 }}>
                          {props.groupByAsset
                            ? p.currencyName ?? p.name
                            : p.hostChain
                              ? `${p.name} on ${p.hostChain.name}`
                              : p.name}
                        </span>
                        {p.isDisabled && <span style={{
                          background: RADOM_COLORS.GRAY_DARK,
                          color: 'black',
                          textTransform: 'uppercase',
                          fontSize: 10,
                          padding: '2px 8px',
                          borderRadius: 5
                        }}>Coming Soon</span>}
                        {!p.isDisabled && unavailable && <span style={{
                          alignSelf: 'flex-start',
                          background: RADOM_COLORS.GRAY_DARK,
                          color: RADOM_COLORS.GRAY_DARKEST,
                          textTransform: 'uppercase',
                          fontSize: 10,
                          padding: '2px 8px',
                          borderRadius: 5
                        }}>{availability?.reason ?? 'Temporarily unavailable'}</span>}
                      </div>
                    </DropdownItem>
                  })}
              </div>
            </div>

            {
              !props.testnetOnly && props.allowTestnets &&
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                position: 'sticky',
                bottom: 0,
                background: 'var(--surface, #ffffff)',
                borderTop: '1px solid var(--surface-pressed, #edf1f7)'
              }}>
                <div
                  onClick={() => {
                    setTestnetMode(!testnetMode)
                    props.setSelectedMethods([])
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 5,
                    padding: 10,
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}>
                  <Switch checked={testnetMode} sizeScale={0.5} />
                  <span style={{
                    color: RADOM_COLORS.GRAY_DARKER,
                    fontSize: 12,
                    textTransform: 'uppercase'
                  }}>Testnet mode</span>
                  <Tooltip message='Use testnets to validate your checkout before moving to production.' />
                </div>
              </div>
            }
          </div>
        }
      />
    </div>
  )
}